import React, { useEffect, useState } from "react";
import { FileUp } from "lucide-react";
import { useModal } from "@/hooks/useModalStore";
import { Upload, Form, Avatar, Button, Input, Modal, Spin, Alert } from "antd";
import { UploadFile, RcFile, UploadProps } from "antd/es/upload/interface";
import { toast } from "sonner";
import { uploadUrl } from "@/config/urls";
import Colors from "@/config/Colors";
import DaggerCard from "@/components/Cards/DaggerCard/DaggerCard";
import { CustomIcons } from "@/assets/index";
import { modelTitle } from "@/styles/ModalStyles";
import { copyObjects } from "@/util/copyObjects";
import {
  useUpdateResponderCompany,
  useCreateResponderCompany,
  useOverViewResponderCompanies,
  useResponderCompanyById,
} from "@/hooks/useResponderCompany";
import DirectorsDetails from "../Forms/ResponderCompanyForms/DirectorsDetails";
import BasicDetails from "../Forms/ResponderCompanyForms/BasicDetails";
import CompanyAddress from "../Forms/ResponderCompanyForms/CompanyAddress";
import CoverageAreas from "../Forms/ResponderCompanyForms/CoverageAreas";
import { useStore } from "zustand";
import LegalDocuments from "../Forms/ResponderCompanyForms/LegalDocuments";
import FleetInformation from "../Forms/ResponderCompanyForms/FleetInformation";
import dayjs from "dayjs";

const { Dragger } = Upload;

function ResponderCompanyModal() {
  const { isOpen, onClose, type, data } = useModal();
  const { activeResponderCompanyId } = data;
  const createCompanyMutation = useCreateResponderCompany();
  const updateCompanyMutation = useUpdateResponderCompany();
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState("");
  const [certificateUrl, setCertificateUrl] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [certificateFileList, setCertificateFileList] = useState<UploadFile[]>(
    [],
  );
  const [isUploading, setIsUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const {
    data: existingCompanyData,
    refetch: refetchExistingCompanyData,
    error,
    isLoading,
  } = useResponderCompanyById(activeResponderCompanyId);

  const isModalOpen =
    isOpen &&
    (type === "createResponderCompany" || type === "editResponderCompany");
  const isEdit = type === "editResponderCompany";
  const editDetails = data?.responderCompany;

  const {
    data: companiesData,
    hasNextPage,
    fetchNextPage,
    isLoading: isLoadingCompanies,
    isFetchingNextPage,
    refetch: refetchCompaniesData,
  } = useOverViewResponderCompanies(10, "", isModalOpen);

  // Add state to collect form data from all steps
  const [formData, setFormData] = useState<{
    basicDetails: any;
    legalDocuments: any;
    coverageAreas: any;
    companyAddress: any;
    directorsDetails: any;
    fleetInformation: any;
  }>({
    basicDetails: {},
    legalDocuments: {},
    coverageAreas: {},
    companyAddress: {},
    directorsDetails: {},
    fleetInformation: {},
  });

  const handleReset = () => {
    form.resetFields();
    setImageUrl("");
    setCertificateUrl("");
    setFileList([]);
    setCertificateFileList([]);
    // Reset form data state
    setFormData({
      basicDetails: {},
      legalDocuments: {},
      coverageAreas: {},
      companyAddress: {},
      directorsDetails: {},
      fleetInformation: {},
    });
  };

  useEffect(() => {
    if (isModalOpen && isEdit && existingCompanyData) {
      const updatedFormData = {
        basicDetails: {
          companyName: existingCompanyData?.companyName ?? "",
          companyDescription: existingCompanyData?.companyDescription ?? "",
          companyImage: existingCompanyData?.companyImage ?? "",
        },
        legalDocuments: {
          psiraUrl: existingCompanyData?.psiraClearanceCertificate ?? "",
          cipcUrl: existingCompanyData?.cipCertificate ?? "",
          taxClearanceUrl: existingCompanyData?.taxClearanceCertificate ?? "",
        },
        coverageAreas: {
          coverageArea: existingCompanyData?.areasCovered
            ? existingCompanyData.areasCovered.map((area, index) => ({
                id: index,
                addressLine1: area?.split(",")?.[0] ?? "",
                addressLine2: area?.split(",")?.[1] ?? "",
                postalCode: area?.split(",")?.[2] ?? "",
              }))
            : [],
        },
        companyAddress: {
          companyAddress: existingCompanyData?.address ?? "",
          proofofAddressUrl: existingCompanyData?.proofOfAddress ?? "",
        },
        directorsDetails: {
          directorFirstName:
            existingCompanyData?.companyDirectors?.[0]?.responderCompanyDirector
              ?.firstName ?? "",
          directorLastName:
            existingCompanyData?.companyDirectors?.[0]?.responderCompanyDirector
              ?.lastName ?? "",
          copyOfDirectorsId:
            existingCompanyData?.companyDirectors?.[0]?.idDocumentUrl ?? "",
          directorDob:
            existingCompanyData?.companyDirectors?.[0]?.responderCompanyDirector
              ?.dateOfBirth ?? "",
          directorEmail:
            existingCompanyData?.companyDirectors?.[0]?.responderCompanyDirector
              ?.emailAddress ?? "",
          directorPhoneNo:
            existingCompanyData?.companyDirectors?.[0]?.responderCompanyDirector
              ?.phoneNumber ?? "",
          directorGender:
            existingCompanyData?.companyDirectors?.[0]?.responderCompanyDirector
              ?.gender ?? "",
        },
        fleetInformation: {
          vehicles12HourShift:
            existingCompanyData.vehiclesOperating12Hours || 0,
          vehicles24_7: existingCompanyData.vehiclesOperating24_7 || 0,
          adHocGaurdingServices:
            existingCompanyData.providesAdhocGuarding || false,
          armedResponseStaff: existingCompanyData.numberOfArmedResponders || 0,
          medicalStaffMembers:
            existingCompanyData.numberOfMedicalResponders || 0,
          otherResponderStaff: 0, // API doesn't provide this field, default to 0
          rate: existingCompanyData.dailyRateGradeCUnarmedGuard || 0,
        },
      };

      setFormData(updatedFormData);

      if (existingCompanyData.companyImage) {
        setImageUrl(existingCompanyData.companyImage);
        setFileList([
          {
            uid: "-1",
            name: "company-image.jpg",
            status: "done",
            url: existingCompanyData.companyImage,
          },
        ]);
      }

      if (existingCompanyData?.psiraClearanceCertificate) {
        setCertificateUrl(existingCompanyData.psiraClearanceCertificate);
        setCertificateFileList([
          {
            uid: "-1",
            name: "psira-certificate.pdf",
            status: "done",
            url: existingCompanyData.psiraClearanceCertificate,
          },
        ]);
      }
    }
  }, [existingCompanyData, isModalOpen, isEdit]);

  const handleClose = async () => {
    handleReset();
    setCurrentStep(0);
    onClose();
  };

  const handleEditCompany = async (allFormData: any) => {
    if (!activeResponderCompanyId) {
      toast.error("Company ID is missing for update.");
      return;
    }

    const combinedCoverageAreas = Array.isArray(allFormData.coverageAreas)
      ? allFormData.coverageAreas.map((area: any) =>
          [area.addressLine1, area.addressLine2, area.postalCode]
            .filter(Boolean)
            .join(", "),
        )
      : existingCompanyData?.areasCovered || []; // Fallback

    const payload: IResponderCompanyUpdateRequest = {
      Id: activeResponderCompanyId,

      // Basic Details
      CompanyName: allFormData.basicDetails.companyName,
      CompanyImage: allFormData.basicDetails.companyImage || null,
      CompanyDescription: allFormData.basicDetails.companyDescription,

      // Company Address
      CompanyAddress: allFormData.companyAddress.companyAddress,
      ProofofAddressUrl: allFormData.companyAddress.proofofAddressUrl,

      // Legal Documents
      PsiraUrl: allFormData.legalDocuments.psiraUrl,
      CipcUrl: allFormData.legalDocuments.cipcUrl,
      TaxClearanceUrl: allFormData.legalDocuments.taxClearanceUrl,

      // Directors Details
      CopyOfDirectorsId: allFormData.directorsDetails.copyOfDirectorsId,
      DirectorFirstName: allFormData.directorsDetails.directorFirstName,
      DirectorLastName: allFormData.directorsDetails.directorLastName,
      DirectorEmail: allFormData.directorsDetails.directorEmail,
      DirectorPhoneNo: allFormData.directorsDetails.directorPhoneNo,
      DirectorGender: allFormData.directorsDetails.directorGender,
      DirectorDOB: dayjs(
        allFormData.directorsDetails.directorDob,
      ).toISOString(),

      // Coverage Areas
      CoverageArea: combinedCoverageAreas,

      // Fleet Information
      Vehicles24_7:
        parseInt(allFormData.fleetInformation.vehicles24_7, 10) || 0,
      Vehicles12HourShift:
        parseInt(allFormData.fleetInformation.vehicles12HourShift, 10) || 0,
      ArmedResponseStaff:
        parseInt(allFormData.fleetInformation.armedResponseStaff, 10) || 0,
      MedicalStaffMembers:
        parseInt(allFormData.fleetInformation.medicalStaffMembers, 10) || 0,
      OtherResponderStaff:
        parseInt(allFormData.fleetInformation.otherResponderStaff, 10) || 0,
      AdHocGaurdingServices:
        allFormData.fleetInformation.adHocGaurdingServices || false,
      Rate: allFormData.fleetInformation.adHocGaurdingServices
        ? parseFloat(allFormData.fleetInformation.rate) || null
        : null,
    };

    try {
      await updateCompanyMutation.mutateAsync(payload);
      toast.success("Company updated successfully.");
      handleClose();
    } catch (error) {
      console.error("Update Company Error:", error);
      // const errorMessage = error.response?.data?.message || error.message || "Failed to update company.";
      // toast.error(errorMessage);
      toast.error(
        "Failed to edit company, please check any missing fields and fill them",
      );
    }
  };

  const handleCreateCompany = async (values: any) => {
    // Combine coverage areas into an array with comma-separated addresses
    const combinedCoverageAreas = Array.isArray(values.coverageAreas)
      ? values.coverageAreas.map((area: any) =>
          [area.addressLine1, area.addressLine2, area.postalCode]
            .filter(Boolean)
            .join(", "),
        )
      : [""];

    const payload: ICreateResponderCompanyDto = {
      // Basic Details
      companyName: values.basicDetails.companyName,
      companyDescription: values.basicDetails.companyDescription,
      companyImage: values.basicDetails.companyImage,
      // Legal Documents
      psiraUrl: values.legalDocuments.psiraUrl,
      cipcUrl: values.legalDocuments.cipcUrl,
      taxClearanceUrl: values.legalDocuments.taxClearanceUrl,
      // Coverage Areas
      coverageArea: combinedCoverageAreas,
      // Directors Details
      directorFirstName: values.directorsDetails.directorFirstName,
      directorLastName: values.directorsDetails.directorLastName,
      copyOfDirectorsId: values.directorsDetails.copyOfDirectorsId,
      directorDob: values.directorsDetails.directorDob,
      directorEmail: values.directorsDetails.directorEmail,
      directorPhoneNo: values.directorsDetails.directorPhoneNo,
      directorGender: values.directorsDetails.directorGender,
      // Company Address
      companyAddress: values.companyAddress.companyAddress,
      proofofAddressUrl: values.companyAddress.proofofAddressUrl,
      // Fleet Information
      vehicles12HourShift: values.fleetInformation.vehicles12HourShift,
      vehicles24_7: values.fleetInformation.vehicles24_7,
      adHocGaurdingServices:
        values.fleetInformation.rate == undefined ? false : true,
      armedResponseStaff: values.fleetInformation.armedResponseStaff,
      medicalStaffMembers: values.fleetInformation.medicalStaffMembers,
      otherResponderStaff: values.fleetInformation.otherResponderStaff,
      rate: values.fleetInformation.rate,
    };

    try {
      const response = await createCompanyMutation.mutateAsync(payload);
      toast.success("Company created successfully.");
      handleClose();
    } catch (error) {
      console.log(error);
    }
  };

  // Update form data handlers for each step
  const handleBasicDetailsSubmit = (values: ICreateResponderCompanyDto) => {
    setFormData((prev) => ({ ...prev, basicDetails: values }));
    handleNext();
  };

  const handleLegalDocumentsSubmit = (values: ICreateResponderCompanyDto) => {
    setFormData((prev) => ({ ...prev, legalDocuments: values }));
    handleNext();
  };

  const handleCoverageAreasSubmit = (values: ICreateResponderCompanyDto) => {
    setFormData((prev) => ({ ...prev, coverageAreas: values }));
    handleNext();
  };

  const handleCompanyAddressSubmit = (values: ICreateResponderCompanyDto) => {
    setFormData((prev) => ({ ...prev, companyAddress: values }));
    handleNext();
  };

  const handleDirectorsDetailsSubmit = (values: ICreateResponderCompanyDto) => {
    setFormData((prev) => ({ ...prev, directorsDetails: values }));
    handleNext();
  };

  const handleFleetInformationSubmit = (fleetInfoValues: any) => {
    const allFormData = {
      ...formData,
      fleetInformation: fleetInfoValues,
    };

    if (isEdit) {
      handleEditCompany(allFormData);
    } else {
      handleCreateCompany(allFormData);
    }
    refetchExistingCompanyData();
    handleClose();
    return allFormData;
  };

  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  // Render the appropriate step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicDetails
            onFormFinish={handleBasicDetailsSubmit}
            onNext={handleNext}
            isLastStep={false}
            initialValues={formData.basicDetails}
          />
        );
      case 1:
        return (
          <LegalDocuments
            onFormFinish={handleLegalDocumentsSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLastStep={false}
            initialValues={formData.legalDocuments}
          />
        );
      case 2:
        return (
          <CoverageAreas
            onFormFinish={handleCoverageAreasSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLastStep={false}
            initialValues={formData.coverageAreas}
          />
        );
      case 3:
        return (
          <CompanyAddress
            onFormFinish={handleCompanyAddressSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLastStep={false}
            initialValues={formData.companyAddress}
          />
        );
      case 4:
        return (
          <DirectorsDetails
            onFormFinish={handleDirectorsDetailsSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLastStep={false}
            initialValues={formData.directorsDetails}
          />
        );
      case 5:
        return (
          <FleetInformation
            onFormFinish={handleFleetInformationSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLastStep={true}
            closeModal={handleClose}
            initialValues={formData.fleetInformation}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      centered
      open={isModalOpen}
      onCancel={() => handleClose()}
      width={550}
      closeIcon={
        <CustomIcons.CloseIcon
          width={30}
          height={30}
          viewBox={null}
          svgColor="#1C274C"
        />
      }
      footer={null}
    >
      <div className="flex w-12 py-3 justify-content-start" style={modelTitle}>
        {isEdit ? "Edit Responder Company" : "+Add Responder Company"}
      </div>
      {isEdit && isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" tip="Loading company data..." />
        </div>
      ) : isEdit && error ? (
        <Alert
          message="Failed to load company data"
          description={
            error?.message ||
            "An error occurred while loading the company data. Please try again."
          }
          type="error"
          showIcon
          className="mb-4"
        />
      ) : (
        renderStep()
      )}
    </Modal>
  );
}

export default ResponderCompanyModal;
